import {
    Typography,
    Box,
    DialogTitle,
    DialogContent,
    Dialog,
    DialogActions,
    Button,
    IconButton,
    SxProps,
    Breakpoint,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { forwardRef, ReactNode, useEffect } from 'react'
import { useTranslation } from 'react-i18next'

interface Props {
    open: boolean
    handleClose(v: boolean): void
    handleSubmit(): void
    title: string
    textButton: string
    children: ReactNode
    handleClickSecondaryButton?(): void
    fullWidth?: boolean
    fullScreen?: boolean
    maxWidth?: Breakpoint
    disabledButton?: boolean
    enableKeyboardHandler?: boolean
    sx?: SxProps
}

const GenericModal = forwardRef<HTMLDivElement, Props>(
    (
        {
            handleClose,
            handleSubmit,
            title,
            textButton,
            children,
            handleClickSecondaryButton,
            open,
            fullWidth,
            fullScreen,
            maxWidth,
            disabledButton,
            enableKeyboardHandler,
            sx,
        },
        ref
    ) => {
        const { t } = useTranslation()

        const keyboardHandler = (e: KeyboardEvent) => {
            if (e.key === 'Enter') {
                e.preventDefault()
                handleSubmit()
            }
        }

        useEffect(() => {
            if(!open || !enableKeyboardHandler) return;

            document.body.addEventListener('keydown', keyboardHandler)

            return () => {
                document.body.removeEventListener('keydown', keyboardHandler)
            }
        }, [open])

        return (
            <Dialog
                open={open}
                onClose={() => handleClose(false)}
                fullWidth={fullWidth}
                fullScreen={fullScreen}
                maxWidth={maxWidth ?? 'xl'}
                className="modal"
                PaperProps={{
                    sx: {
                        maxWidth: fullScreen
                            ? undefined
                            : fullWidth
                              ? { xs: 'calc(100% - 32px)', md: '1400px' }
                              : { xs: 'calc(100% - 32px)', md: '900px' },
                        minWidth: {
                            xs: 'auto',
                            sm: fullWidth ? 'auto' : '380px',
                        },
                    },
                }}
            >
                <DialogTitle sx={{ pr: '16px' }}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: '16px',
                        }}
                    >
                        {!!title && (
                            <Typography
                                sx={{
                                    color: 'text.primary',
                                    fontSize: { xs: '17px', sm: '19px' },
                                    fontWeight: 600,
                                    textAlign: 'left',
                                    flex: 1,
                                }}
                            >
                                {t(title)}
                            </Typography>
                        )}
                        <IconButton
                            aria-label="close"
                            onClick={() => handleClose(false)}
                            sx={{
                                color: 'text.secondary',
                                borderRadius: '8px',
                            }}
                            size="small"
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent ref={ref} dividers={false}>
                    <Box
                        sx={{
                            width: '100%',
                            height: '100%',
                            ...sx,
                        }}
                    >
                        {children}
                    </Box>
                </DialogContent>
                {(textButton || handleClickSecondaryButton) && (
                    <DialogActions
                        sx={{
                            flexDirection: {
                                xs: 'column-reverse',
                                md: 'row',
                            },
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: { xs: '8px', md: '12px' },
                        }}
                    >
                        {handleClickSecondaryButton && (
                            <Button
                                variant="outlined"
                                color="inherit"
                                sx={{
                                    textTransform: 'none',
                                    width: { xs: '100%', md: 'auto' },
                                    flex: { md: 1 },
                                    minWidth: { md: '180px' },
                                    maxWidth: { md: '360px' },
                                    py: '9px',
                                }}
                                onClick={handleClickSecondaryButton}
                            >
                                {t('Volver')}
                            </Button>
                        )}
                        {textButton && (
                            <Button
                                variant="contained"
                                color="primary"
                                sx={{
                                    width: { xs: '100%', md: 'auto' },
                                    flex: { md: 1 },
                                    minWidth: { md: '180px' },
                                    maxWidth: { md: '360px' },
                                    textTransform: 'none',
                                    py: '10px',
                                    fontSize: '15px',
                                }}
                                onClick={() => {
                                    handleSubmit()
                                }}
                                disabled={disabledButton}
                            >
                                {t(textButton)}
                            </Button>
                        )}
                    </DialogActions>
                )}
            </Dialog>
        )
    }
)

export default GenericModal
